import React, { useMemo, FC, useRef, useState, useEffect } from "react";
import { ChevronDown, Upload, Download, Scissors, AlertTriangle, Languages, Loader2, Plus, Eye, EyeOff, Sparkles } from "lucide-react";
import type { Clip, SubtitleStyle, Subtitle } from "../../types";
import { formatDuration, parseSrtFile, parseJsonFile, subtitlesToSrt, secondsToSrtTimestamp, srtTimestampToSeconds, isSubtitleTooLong, splitAllLongSubtitles } from "../../utils";
// FFmpegService removed - audio extraction happens server-side now
import { fixSubtitleOverlaps } from "../../utils/subtitle-helpers";

interface SubtitlesTabProps {
    subtitleStyle: SubtitleStyle;
    setSubtitleStyle: React.Dispatch<React.SetStateAction<SubtitleStyle>>;
    onSeekRequest: (time: number) => void;
    activeSubtitleLanguage: string;
    onActiveSubtitleLanguageChange: (lang: string) => void;
    onTranslateSubtitles?: (targetLang: string) => void;
    onGenerateSubtitles?: (clip: Clip) => Promise<void>;
    translationsInProgress?: Set<string>;
    allClips: Clip[];
    setAllClips: React.Dispatch<React.SetStateAction<Clip[]>>;
    selectedClipId: string | null;
    setSelectedClipId: (id: string | null) => void;
    selectedSubtitleId?: number | string | null;
    projectName?: string;
}

const SubtitlesTab: FC<SubtitlesTabProps> = ({
    subtitleStyle,
    setSubtitleStyle,
    onSeekRequest,
    activeSubtitleLanguage,
    onActiveSubtitleLanguageChange,
    onTranslateSubtitles,
    onGenerateSubtitles,
    translationsInProgress = new Set(),
    allClips,
    setAllClips,
    selectedClipId,
    setSelectedClipId,
    selectedSubtitleId,
    projectName = "Projet",
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const extractInputRef = useRef<HTMLInputElement>(null);
    const subtitleRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());

    // Auto-scroll to active subtitle
    useEffect(() => {
        if (selectedSubtitleId !== undefined && selectedSubtitleId !== null && subtitleRefs.current.has(selectedSubtitleId)) {
            const el = subtitleRefs.current.get(selectedSubtitleId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedSubtitleId]);

    const [isExtracting, setIsExtracting] = useState(false);
    const [extractProgress, setExtractProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isStyleOpen, setIsStyleOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showReplace, setShowReplace] = useState(false);
    const [replaceQuery, setReplaceQuery] = useState("");

    const selectedClip = useMemo(() => allClips.find(c => c.id === selectedClipId), [allClips, selectedClipId]);

    const handleReplaceAll = () => {
        if (!selectedClip || !searchQuery) return;

        setAllClips(prevClips => {
            const newClips = [...prevClips];
            const targetClipIndex = newClips.findIndex(c => c.id === selectedClip.id);
            if (targetClipIndex === -1) return prevClips;

            const updatedClip = { ...newClips[targetClipIndex] };
            if (!updatedClip.subtitles) return prevClips;

            const newSubtitles = { ...updatedClip.subtitles };
            const currentLangSubs = newSubtitles[activeSubtitleLanguage];
            if (!currentLangSubs) return prevClips;

            let replacementsCount = 0;
            const updatedLangSubs = currentLangSubs.map(sub => {
                // Perform a case-insensitive replacement globally in the subtitle text
                const regex = new RegExp(searchQuery, 'gi');
                if (regex.test(sub.text)) {
                    replacementsCount++;
                    return {
                        ...sub,
                        text: sub.text.replace(regex, replaceQuery)
                    };
                }
                return sub;
            });

            if (replacementsCount > 0) {
                newSubtitles[activeSubtitleLanguage] = updatedLangSubs;
                updatedClip.subtitles = newSubtitles;
                newClips[targetClipIndex] = updatedClip;
                return newClips;
            }

            return prevClips; // No changes made
        });

        // Optionally clear the inputs after replacement, or leave them for user to verify
        // setSearchQuery("");
        // setReplaceQuery("");
        // setShowReplace(false);
    };
    const clipHasAnySubtitles = (clip?: Clip | null) => {
        if (!clip?.subtitles) return false;
        return Object.values(clip.subtitles).some((subs: any) => Array.isArray(subs) && subs.length > 0);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (!file) return;

        // Check if video
        if (!file.type.startsWith('video/') && !file.name.match(/\.(mkv|avi|mov|mp4)$/i)) {
            alert('Veuillez déposer un fichier vidéo.');
            return;
        }

        // Extraction audio désactivée côté client - tout se fait côté serveur
        alert('L\'extraction audio se fait automatiquement côté serveur lors de l\'ingestion.\n\nDéposez votre vidéo dans le projet pour déclencher le traitement.');
    };


    const clipsWithSubtitles = useMemo(() => {
        const clipsWithSubs = allClips.filter(c => (c.type === 'video' || c.type === 'audio') && c.subtitles && c.url);
        const groupedByUrl = new Map<string, Clip>();

        for (const clip of clipsWithSubs) {
            // Ne garder que le premier clip de chaque URL (évite les doublons)
            if (!groupedByUrl.has(clip.url!)) {
                groupedByUrl.set(clip.url!, clip);
            }
        }

        const uniqueClips = Array.from(groupedByUrl.values()).sort((a, b) => a.start - b.start);

        // Dédupliquer une dernière fois par ID au cas où (sécurité)
        const seenIds = new Set<string>();
        return uniqueClips.filter(clip => {
            if (seenIds.has(clip.id)) return false;
            seenIds.add(clip.id);
            return true;
        });
    }, [allClips]);

    // Auto-sélectionner un clip pertinent pour l'édition des sous-titres.
    useEffect(() => {
        if (!selectedClip) {
            if (clipsWithSubtitles.length > 0) {
                setSelectedClipId(clipsWithSubtitles[0].id);
            } else {
                const firstMediaClip = allClips.find(c => c.type === 'video' || c.type === 'audio');
                if (firstMediaClip) {
                    setSelectedClipId(firstMediaClip.id);
                }
            }
            return;
        }

        // If the selected clip has no subtitles but others do, switch to a subtitle-capable clip.
        if (!clipHasAnySubtitles(selectedClip) && clipsWithSubtitles.length > 0) {
            const fallback = clipsWithSubtitles[0];
            if (fallback && fallback.id !== selectedClip.id) {
                setSelectedClipId(fallback.id);
            }
        }
    }, [selectedClip, clipsWithSubtitles, allClips, setSelectedClipId]);
    const displayedSubtitles = useMemo(() => {
        const allSubs = new Map<string | number, Subtitle>();

        for (const clip of allClips) {
            if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
                const langSubs = clip.subtitles[activeSubtitleLanguage];
                for (const sub of langSubs) {
                    if (allSubs.has(sub.id)) {
                        const existing = allSubs.get(sub.id)!;
                        existing.start = Math.min(existing.start, sub.start);
                        existing.end = Math.max(existing.end, sub.end);
                        if (sub.text.length > existing.text.length) {
                            existing.text = sub.text;
                        }
                        if (sub.hidden) {
                            existing.hidden = true;
                        }
                    } else {
                        allSubs.set(sub.id, { ...sub });
                    }
                }
            }
        }

        return Array.from(allSubs.values()).sort((a, b) => a.start - b.start);
    }, [allClips, activeSubtitleLanguage]);

    const handleSubtitleTextChange = (subtitleId: string | number, newText: string) => {
        setAllClips(prev => {
            const newClips = [...prev];
            const clipToUpdateIndex = newClips.findIndex(c =>
                c.subtitles &&
                c.subtitles[activeSubtitleLanguage] &&
                c.subtitles[activeSubtitleLanguage].some(s => s.id === subtitleId)
            );

            if (clipToUpdateIndex === -1) return prev;

            const clipToUpdate = { ...newClips[clipToUpdateIndex] };
            const newSubtitles = { ...clipToUpdate.subtitles! };
            const langSubs = newSubtitles[activeSubtitleLanguage];

            if (langSubs) {
                const updatedLangSubs = langSubs.map(s => s.id === subtitleId ? { ...s, text: newText } : s);
                newSubtitles[activeSubtitleLanguage] = updatedLangSubs;
                clipToUpdate.subtitles = newSubtitles;
                newClips[clipToUpdateIndex] = clipToUpdate;
            }

            return newClips;
        });
    };

    const handleSubtitleTimingChange = (subtitleId: string | number, field: 'start' | 'end', value: string) => {
        const seconds = srtTimestampToSeconds(value);
        if (isNaN(seconds)) return;

        setAllClips(prev => {
            const newClips = [...prev];
            let modified = false;

            for (let i = 0; i < newClips.length; i++) {
                const clip = newClips[i];
                if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
                    const langSubs = clip.subtitles[activeSubtitleLanguage];
                    const subIndex = langSubs.findIndex(s => s.id === subtitleId);
                    if (subIndex !== -1) {
                        const updatedClip = { ...clip };
                        const updatedSubtitles = { ...updatedClip.subtitles! };
                        let updatedLangSubs = [...langSubs];

                        updatedLangSubs[subIndex] = { ...updatedLangSubs[subIndex], [field]: seconds };

                        // Sort by start to maintain order immediately
                        updatedLangSubs.sort((a, b) => a.start - b.start);

                        // Fix overlaps if timing changed, but prioritize user's manual edit
                        for (let j = 0; j < updatedLangSubs.length - 1; j++) {
                            if (updatedLangSubs[j].end > updatedLangSubs[j + 1].start) {
                                if (updatedLangSubs[j].id === subtitleId && field === 'end') {
                                    // Push the next subtitle start to respect current sub's new end
                                    updatedLangSubs[j + 1].start = updatedLangSubs[j].end;
                                    // Ensure next subtitle doesn't collapse
                                    if (updatedLangSubs[j + 1].end < updatedLangSubs[j + 1].start) {
                                        updatedLangSubs[j + 1].end = updatedLangSubs[j + 1].start + 1.0;
                                    }
                                } else {
                                    // Default behavior: clamp previous subtitle's end before next start
                                    updatedLangSubs[j].end = updatedLangSubs[j + 1].start;
                                }
                            }
                        }

                        updatedSubtitles[activeSubtitleLanguage] = updatedLangSubs;
                        updatedClip.subtitles = updatedSubtitles;
                        newClips[i] = updatedClip;
                        modified = true;
                    }
                }
            }

            return modified ? newClips : prev;
        });
    };

    const handleToggleSubtitleVisibility = (subtitleId: string | number) => {
        setAllClips(prev => {
            const newClips = [...prev];
            let modified = false;

            // First, find the current hidden state of the subtitle to toggle uniformly
            let targetHiddenState = false;
            for (const clip of newClips) {
                if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
                    const sub = clip.subtitles[activeSubtitleLanguage].find(s => s.id === subtitleId);
                    if (sub) {
                        targetHiddenState = !sub.hidden;
                        break;
                    }
                }
            }

            for (let i = 0; i < newClips.length; i++) {
                const clip = newClips[i];
                if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
                    const langSubs = clip.subtitles[activeSubtitleLanguage];
                    const subIndex = langSubs.findIndex(s => s.id === subtitleId);
                    if (subIndex !== -1) {
                        const updatedClip = { ...clip };
                        const updatedSubtitles = { ...updatedClip.subtitles! };
                        const updatedLangSubs = [...langSubs];

                        updatedLangSubs[subIndex] = { ...updatedLangSubs[subIndex], hidden: targetHiddenState };

                        updatedSubtitles[activeSubtitleLanguage] = updatedLangSubs;
                        updatedClip.subtitles = updatedSubtitles;
                        newClips[i] = updatedClip;
                        modified = true;
                    }
                }
            }

            return modified ? newClips : prev;
        });
    };

    const handleImportSrt = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !selectedClip) return;

        try {
            const content = await file.text();
            const fileExtension = file.name.toLowerCase().split('.').pop();

            let parsedSubtitles: { id: number; start: number; end: number; text: string }[];

            if (fileExtension === 'json') {
                parsedSubtitles = parseJsonFile(content);
            } else {
                // Default to SRT parser
                parsedSubtitles = parseSrtFile(content);
            }

            if (parsedSubtitles.length === 0) {
                alert('Aucun sous-titre valide trouvé dans le fichier.');
                return;
            }

            setAllClips(prev => {
                const newClips = [...prev];
                const targetClips = newClips.filter(c => c.id === selectedClip.id || (selectedClip.url && c.url === selectedClip.url));

                if (targetClips.length === 0) return prev;

                targetClips.forEach(clip => {
                    const clipIndex = newClips.findIndex(c => c.id === clip.id);
                    if (clipIndex !== -1) {
                        const updatedClip = { ...newClips[clipIndex] };
                        updatedClip.subtitles = {
                            ...(updatedClip.subtitles || {}),
                            [activeSubtitleLanguage]: fixSubtitleOverlaps(parsedSubtitles)
                        };
                        newClips[clipIndex] = updatedClip;
                    }
                });

                return newClips;
            });

            alert(`${parsedSubtitles.length} sous-titres importés avec succès!`);
        } catch (error) {
            console.error('Erreur lors de l\'importation du fichier:', error);
            alert('Erreur lors de l\'importation du fichier. Vérifiez le format du fichier.');
        }

        // Reset input
        if (event.target) {
            event.target.value = '';
        }
    };

    const handleExportSrt = () => {
        if (!displayedSubtitles.length) {
            alert('Aucun sous-titre à exporter.');
            return;
        }

        try {
            const fixedSubtitles = fixSubtitleOverlaps(displayedSubtitles);
            const srtContent = subtitlesToSrt(fixedSubtitles);
            const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Format project name for safe filename
            const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            link.download = `${safeProjectName}_${activeSubtitleLanguage}.srt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erreur lors de l\'exportation:', error);
            alert('Erreur lors de l\'exportation du fichier SRT.');
        }
    };

    const handleSplitLongSubtitles = () => {
        if (!displayedSubtitles.length) {
            alert('Aucun sous-titre à traiter.');
            return;
        }

        // Compter combien de sous-titres sont trop longs
        const longSubtitles = displayedSubtitles.filter(sub => isSubtitleTooLong(sub));

        if (longSubtitles.length === 0) {
            alert('Aucun sous-titre trop long détecté. Tous les sous-titres respectent les bonnes pratiques !');
            return;
        }

        const confirmed = window.confirm(
            `${longSubtitles.length} sous-titre${longSubtitles.length > 1 ? 's sont' : ' est'} trop long${longSubtitles.length > 1 ? 's' : ''}.\n\n` +
            `Voulez-vous diviser le texte en segments plus courts ?\n\n` +
            `⚠️ IMPORTANT :\n` +
            `Les segments gardent le timing original.\n` +
            `Vous devrez ajuster manuellement les timings\n` +
            `(Début/Fin) en écoutant la vidéo.\n\n` +
            `Division intelligente aux ponctuations.\n` +
            `Max 84 caractères par segment (2 lignes × 42).`
        );

        if (!confirmed) return;

        try {
            setAllClips(prev => {
                const newClips = [...prev];
                let hasChanges = false;

                for (let i = 0; i < newClips.length; i++) {
                    const clip = newClips[i];
                    if (clip.subtitles && clip.subtitles[activeSubtitleLanguage]) {
                        // Diviser uniquement les sous-titres de ce clip
                        const currentLangSubs = clip.subtitles[activeSubtitleLanguage];
                        const clipLongSubs = currentLangSubs.filter(sub => isSubtitleTooLong(sub));

                        if (clipLongSubs.length > 0) {
                            const splitSubtitles = splitAllLongSubtitles(currentLangSubs);
                            newClips[i] = {
                                ...clip,
                                subtitles: {
                                    ...clip.subtitles,
                                    [activeSubtitleLanguage]: splitSubtitles
                                }
                            };
                            hasChanges = true;
                        }
                    }
                }

                return hasChanges ? newClips : prev;
            });

            alert(
                `✅ Texte divisé en segments !\n\n` +
                `• ${longSubtitles.length} sous-titre${longSubtitles.length > 1 ? 's' : ''} divisé${longSubtitles.length > 1 ? 's' : ''}\n` +
                `⚠️ PROCHAINE ÉTAPE :\n` +
                `Ajustez les timings (Début/Fin) de chaque\n` +
                `segment en écoutant la vidéo pour que les\n` +
                `sous-titres correspondent exactement à l'audio.`
            );
        } catch (error) {
            console.error('Erreur lors de la division des sous-titres:', error);
            alert('Erreur lors de la division des sous-titres.');
        }
    };

    const handleExtractAudio = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Extraction audio désactivée côté client - tout se fait côté serveur
        alert('L\'extraction audio se fait automatiquement côté serveur lors de l\'ingestion.\n\nDéposez votre vidéo dans le projet pour déclencher le traitement.');
        if (extractInputRef.current) {
            extractInputRef.current.value = '';
        }
    };

    const handleAddSubtitle = (index: number) => {
        setAllClips(prev => {
            const newClips = [...prev];

            // Determine which clip should receive the new subtitle
            let targetClipIndex = -1;

            if (index >= 0 && index < displayedSubtitles.length) {
                // Find the clip that owns the subtitle at 'index'
                const refSubId = displayedSubtitles[index].id;
                targetClipIndex = newClips.findIndex(c =>
                    c.subtitles &&
                    c.subtitles[activeSubtitleLanguage] &&
                    c.subtitles[activeSubtitleLanguage].some(s => s.id === refSubId)
                );
            }

            // Fallbacks if we couldn't find a clip via index
            if (targetClipIndex === -1) {
                if (selectedClip) {
                    targetClipIndex = newClips.findIndex(c => c.id === selectedClip.id);
                } else {
                    // Just take the first clip that has subtitles, or the first video clip
                    targetClipIndex = newClips.findIndex(c => c.subtitles && c.subtitles[activeSubtitleLanguage]);
                    if (targetClipIndex === -1) {
                        targetClipIndex = newClips.findIndex(c => c.type === 'video' || c.type === 'audio');
                    }
                }
            }

            if (targetClipIndex === -1) {
                alert("Impossible d'ajouter un sous-titre : aucun clip trouvé sur la timeline.");
                return prev;
            }

            const updatedClip = { ...newClips[targetClipIndex] };
            const newSubtitles = { ...(updatedClip.subtitles || {}) };
            const currentLangSubs = [...(newSubtitles[activeSubtitleLanguage] || [])];

            let newStart = 0;
            let newEnd = 2; // Default duration 2s

            if (index === -1) {
                // Add at global start
                if (displayedSubtitles.length > 0) {
                    newStart = 0;
                    newEnd = Math.max(0.5, displayedSubtitles[0].start - 0.2);
                }
            } else if (index >= displayedSubtitles.length - 1) {
                // Add at global end
                if (displayedSubtitles.length > 0) {
                    const lastSub = displayedSubtitles[displayedSubtitles.length - 1];
                    newStart = lastSub.end + 0.2;
                    newEnd = newStart + 2;
                }
            } else {
                // Insert between two subtitles globally
                const prevSub = displayedSubtitles[index];
                const nextSub = displayedSubtitles[index + 1];

                newStart = prevSub.end + 0.2;
                const gapEnd = nextSub.start - 0.2;

                if (gapEnd > newStart) {
                    newEnd = gapEnd;
                } else {
                    newEnd = newStart + 1.0;
                }
            }

            const newSub: Subtitle = {
                id: Date.now(), // Generate a unique ID based on timestamp
                start: newStart,
                end: newEnd,
                text: "Nouveau sous-titre"
            };

            // Add to the chosen clip's subtitles
            currentLangSubs.push(newSub);

            // Re-sort and fix any overlaps for this specific clip
            currentLangSubs.sort((a, b) => a.start - b.start);
            newSubtitles[activeSubtitleLanguage] = fixSubtitleOverlaps(currentLangSubs);

            updatedClip.subtitles = newSubtitles;
            newClips[targetClipIndex] = updatedClip;
            return newClips;
        });
    };

    return (
        <div id="panel-subtitles" role="tabpanel" aria-labelledby="tab-subtitles" className="h-full flex flex-col">
            <input
                ref={fileInputRef}
                type="file"
                accept=".srt,.json"
                onChange={handleFileChange}
                className="hidden"
            />
            <input
                ref={extractInputRef}
                type="file"
                accept="video/*,.mkv,.mov,.avi"
                onChange={handleExtractAudio}
                className="hidden"
            />
            <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex justify-between items-start mb-1">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">Éditeur de Sous-titres</h2>
                        <p className="text-xs text-gray-500">Modifiez le texte et l'apparence</p>
                    </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={handleImportSrt}
                        disabled={!selectedClip}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Importer un fichier SRT ou JSON"
                    >
                        <Upload className="w-3.5 h-3.5" />
                        Importer
                    </button>
                    <button
                        onClick={handleExportSrt}
                        disabled={!selectedClip || !displayedSubtitles.length}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Exporter vers SRT"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Exporter
                    </button>
                    {selectedClip?.transcriptUrl && (
                        <a
                            href={selectedClip.transcriptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            title="Voir le transcript complet (Texte Brut)"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Transcript
                        </a>
                    )}
                    {onGenerateSubtitles && (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => {
                                    if (window.confirm("Êtes-vous sûr de vouloir re-extraire l'audio et relancer la génération des sous-titres ? Cela écrasera les sous-titres actuels.")) {
                                        selectedClip && onGenerateSubtitles(selectedClip);
                                    }
                                }}
                                disabled={!selectedClip || selectedClip.isProcessingSubtitles}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                                title="Re-extraire l'audio et générer les sous-titres automatiques"
                            >
                                {selectedClip?.isProcessingSubtitles ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                Générer Auto
                            </button>
                            {selectedClip?.isProcessingSubtitles && (
                                <button
                                    onClick={() => {
                                        setAllClips(prev => prev.map(c =>
                                            c.id === selectedClip.id ? { ...c, isProcessingSubtitles: false } : c
                                        ));
                                    }}
                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors"
                                    title="Annuler le chargement (Débloquer le bouton)"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>
            {selectedClip ? (
                <>
                    <div className="flex-1 min-h-0 flex flex-col p-4 space-y-4">
                        {/* Fixed header: Style, Search, Languages — NOT scrollable */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <button
                                onClick={() => setIsStyleOpen(!isStyleOpen)}
                                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500">STYLE GLOBAL</h3>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isStyleOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isStyleOpen && (
                                <div className="p-4 space-y-4 border-t border-gray-200">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1.5">Police</label>
                                            <div className="relative"><select value={subtitleStyle.fontFamily} onChange={e => setSubtitleStyle(s => ({ ...s, fontFamily: (e.target as HTMLSelectElement).value }))} className="w-full text-sm p-2.5 pr-8 border border-gray-300 bg-gray-100 text-black rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-black"><option>Arial, sans-serif</option><option>Verdana, sans-serif</option><option>Georgia, serif</option><option>"Courier New", monospace</option><option>"Times New Roman", serif</option></select><ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1.5">Effet</label>
                                            <div className="relative"><select value={subtitleStyle.shadowType} onChange={e => setSubtitleStyle(s => ({ ...s, shadowType: (e.target as HTMLSelectElement).value as SubtitleStyle['shadowType'] }))} className="w-full text-sm p-2.5 pr-8 border border-gray-300 bg-gray-100 text-black rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-black"><option value="none">Aucun</option><option value="shadow">Ombre</option><option value="outline">Contour</option></select><ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /></div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 items-center pt-2">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1.5">Taille ({subtitleStyle.fontSize}px)</label>
                                            <input type="range" min="24" max="96" step="2" value={subtitleStyle.fontSize} onChange={e => setSubtitleStyle(s => ({ ...s, fontSize: Number((e.target as HTMLInputElement).value) }))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1.5">Couleur</label>
                                            <div className="relative w-full h-10 p-1 border border-gray-300 bg-gray-100 rounded-lg"><div className="w-full h-full rounded-sm overflow-hidden"><input type="color" value={subtitleStyle.color} onChange={e => setSubtitleStyle(s => ({ ...s, color: (e.target as HTMLInputElement).value }))} className="w-full h-full border-none cursor-pointer appearance-none bg-transparent" style={{ width: 'calc(100% + 8px)', height: 'calc(100% + 8px)', margin: '-4px' }} /></div></div>
                                        </div>
                                    </div>
                                    {subtitleStyle.shadowType === 'outline' && (
                                        <div className="pt-2">
                                            <label className="text-sm font-medium text-gray-700 block mb-1.5">
                                                Epaisseur du contour ({(subtitleStyle.outlineWidth ?? 1.5).toFixed(1)}px)
                                            </label>
                                            <input
                                                type="range"
                                                min="0.5"
                                                max="8"
                                                step="0.5"
                                                value={subtitleStyle.outlineWidth ?? 1.5}
                                                onChange={e => setSubtitleStyle(s => ({ ...s, outlineWidth: Number((e.target as HTMLInputElement).value) }))}
                                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}
                                    <div className="pt-2">
                                        <label className="text-sm font-medium text-gray-700 block mb-1.5">Position Verticale ({subtitleStyle.positionY}%)</label>
                                        <input
                                            type="range" min="5" max="95" step="1"
                                            value={subtitleStyle.positionY}
                                            onChange={e => setSubtitleStyle(s => ({ ...s, positionY: Number((e.target as HTMLInputElement).value) }))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                    <div className="pt-2">
                                        <label className="text-sm font-medium text-gray-700 block mb-1.5">Marge Horizontale ({subtitleStyle.padding}%)</label>
                                        <input
                                            type="range" min="0" max="40" step="1"
                                            value={subtitleStyle.padding || 5}
                                            onChange={e => setSubtitleStyle(s => ({ ...s, padding: Number((e.target as HTMLInputElement).value) }))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Search and Replace */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500">Rechercher & Remplacer</h3>
                                <button
                                    onClick={() => setShowReplace(!showReplace)}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    {showReplace ? "Masquer Remplacer" : "Afficher Remplacer"}
                                </button>
                            </div>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher..."
                                    className="w-full text-sm p-2.5 pl-9 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                                />
                                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            {showReplace && (
                                <div className="space-y-2 pt-1">
                                    <input
                                        type="text"
                                        value={replaceQuery}
                                        onChange={(e) => setReplaceQuery(e.target.value)}
                                        placeholder="Remplacer par..."
                                        className="w-full text-sm p-2.5 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                                    />
                                    <button
                                        onClick={handleReplaceAll}
                                        disabled={!searchQuery || !selectedClip}
                                        className="w-full py-2 bg-black text-white text-xs font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Tout remplacer
                                    </button>
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500">TEXTE DU CLIP</h3>
                                <div className="flex items-center gap-3">
                                    {/* Français */}
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onActiveSubtitleLanguageChange('fr')} className={`p-1 text-lg rounded-full transition-opacity ${activeSubtitleLanguage !== 'fr' ? 'opacity-30 hover:opacity-70' : ''}`} title="Français">🇫🇷</button>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles('fr')}
                                                disabled={translationsInProgress.has('fr')}
                                                className="p-1 text-xs bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors relative"
                                                title={translationsInProgress.has('fr') ? "Traduction en cours..." : "Traduire en français"}
                                            >
                                                {translationsInProgress.has('fr') ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Languages className="w-3 h-3" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {/* English */}
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onActiveSubtitleLanguageChange('en')} className={`p-1 text-lg rounded-full transition-opacity ${activeSubtitleLanguage !== 'en' ? 'opacity-30 hover:opacity-70' : ''}`} title="English">🇬🇧</button>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles('en')}
                                                disabled={translationsInProgress.has('en')}
                                                className="p-1 text-xs bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors relative"
                                                title={translationsInProgress.has('en') ? "Traduction en cours..." : "Traduire en anglais"}
                                            >
                                                {translationsInProgress.has('en') ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Languages className="w-3 h-3" />
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {/* Español */}
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onActiveSubtitleLanguageChange('es')} className={`p-1 text-lg rounded-full transition-opacity ${activeSubtitleLanguage !== 'es' ? 'opacity-30 hover:opacity-70' : ''}`} title="Español">🇪🇸</button>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles('es')}
                                                disabled={translationsInProgress.has('es')}
                                                className="p-1 text-xs bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors relative"
                                                title={translationsInProgress.has('es') ? "Traduction en cours..." : "Traduire en espagnol"}
                                            >
                                                {translationsInProgress.has('es') ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Languages className="w-3 h-3" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {/* Portugais */}
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onActiveSubtitleLanguageChange('pt')} className={`p-1 text-lg rounded-full transition-opacity ${activeSubtitleLanguage !== 'pt' ? 'opacity-30 hover:opacity-70' : ''}`} title="Portugais">🇵🇹</button>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles('pt')}
                                                disabled={translationsInProgress.has('pt')}
                                                className="p-1 text-xs bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors relative"
                                                title={translationsInProgress.has('pt') ? "Traduction en cours..." : "Traduire en portugais"}
                                            >
                                                {translationsInProgress.has('pt') ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Languages className="w-3 h-3" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {/* Deutsch */}
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onActiveSubtitleLanguageChange('de')} className={`p-1 text-lg rounded-full transition-opacity ${activeSubtitleLanguage !== 'de' ? 'opacity-30 hover:opacity-70' : ''}`} title="Deutsch">🇩🇪</button>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles('de')}
                                                disabled={translationsInProgress.has('de')}
                                                className="p-1 text-xs bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors relative"
                                                title={translationsInProgress.has('de') ? "Traduction en cours..." : "Traduire en allemand"}
                                            >
                                                {translationsInProgress.has('de') ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Languages className="w-3 h-3" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {/* Italiano */}
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => onActiveSubtitleLanguageChange('it')} className={`p-1 text-lg rounded-full transition-opacity ${activeSubtitleLanguage !== 'it' ? 'opacity-30 hover:opacity-70' : ''}`} title="Italiano">🇮🇹</button>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles('it')}
                                                disabled={translationsInProgress.has('it')}
                                                className="p-1 text-xs bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors relative"
                                                title={translationsInProgress.has('it') ? "Traduction en cours..." : "Traduire en italien"}
                                            >
                                                {translationsInProgress.has('it') ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Languages className="w-3 h-3" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable subtitle list */}
                        <div className="flex-1 min-h-0 overflow-y-auto">
                            <div className="space-y-3">
                                {displayedSubtitles.length > 0 ? (
                                    <>
                                        <div className="flex justify-center -my-2 relative z-10">
                                            <button
                                                onClick={() => handleAddSubtitle(-1)}
                                                className="p-1 bg-gray-300 text-white rounded-full shadow-sm hover:bg-black hover:scale-110 transition-all"
                                                title="Ajouter un sous-titre au début"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        {displayedSubtitles
                                            .filter(sub => !searchQuery || sub.text.toLowerCase().includes(searchQuery.toLowerCase()))
                                            .map((sub, index) => {
                                                return (
                                                    <React.Fragment key={sub.id}>
                                                        <div
                                                            ref={(el) => {
                                                                if (el) subtitleRefs.current.set(sub.id, el);
                                                                else subtitleRefs.current.delete(sub.id);
                                                            }}
                                                            className={`p-3 border rounded-lg space-y-2 transition-all ${selectedSubtitleId === sub.id ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/50' : ''
                                                                } ${sub.hidden
                                                                    ? 'bg-gray-100 border-gray-300 opacity-50'
                                                                    : 'bg-white border-gray-200'
                                                                }`}>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => handleToggleSubtitleVisibility(sub.id)}
                                                                    className={`p-1.5 rounded-md transition-colors ${sub.hidden
                                                                        ? 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                                                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                                                        }`}
                                                                    title={sub.hidden ? 'Afficher ce sous-titre' : 'Masquer ce sous-titre'}
                                                                >
                                                                    {sub.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                                </button>
                                                                <div className="flex-1 grid grid-cols-2 gap-2">
                                                                    <div>
                                                                        <label className="text-xs font-medium text-gray-600 block mb-1">Début</label>
                                                                        <input
                                                                            type="text"
                                                                            key={`start-${sub.id}-${sub.start}`}
                                                                            defaultValue={secondsToSrtTimestamp(sub.start)}
                                                                            onFocus={() => onSeekRequest(sub.start)}
                                                                            onBlur={e => handleSubtitleTimingChange(sub.id, 'start', e.target.value)}
                                                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                                            className="w-full text-xs font-mono p-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                                                            placeholder="00:00:00,000"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-xs font-medium text-gray-600 block mb-1">Fin</label>
                                                                        <input
                                                                            type="text"
                                                                            key={`end-${sub.id}-${sub.end}`}
                                                                            defaultValue={secondsToSrtTimestamp(sub.end)}
                                                                            onFocus={() => onSeekRequest(sub.end)}
                                                                            onBlur={e => handleSubtitleTimingChange(sub.id, 'end', e.target.value)}
                                                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                                            className="w-full text-xs font-mono p-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                                                            placeholder="00:00:00,000"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => onSeekRequest(sub.start)}
                                                                    className="mt-5 px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                                                    title="Aller à ce sous-titre"
                                                                >
                                                                    ▶
                                                                </button>
                                                            </div>
                                                            <textarea
                                                                value={sub.text}
                                                                onChange={e => handleSubtitleTextChange(sub.id, (e.target as HTMLTextAreaElement).value)}
                                                                onFocus={() => onSeekRequest(sub.start)}
                                                                className="w-full text-sm p-3 bg-gray-50 text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
                                                                rows={Math.max(2, Math.ceil(sub.text.length / 40))}
                                                                placeholder="Texte du sous-titre..."
                                                            />
                                                        </div>
                                                        {/* Add Button Between */}
                                                        <div className="flex justify-center -my-2 relative z-10">
                                                            <button
                                                                onClick={() => handleAddSubtitle(index)}
                                                                className="p-1 bg-gray-300 text-white rounded-full shadow-sm hover:bg-black hover:scale-110 transition-all"
                                                                title="Insérer un sous-titre ici"
                                                            >
                                                                <Plus className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </>
                                ) : (
                                    <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
                                        <p className="text-sm font-medium text-amber-800 mb-2">
                                            Aucun sous-titre en {{ fr: 'français', en: 'anglais', es: 'espagnol', de: 'allemand', it: 'italien', pt: 'portugais' }[activeSubtitleLanguage] || activeSubtitleLanguage}
                                        </p>
                                        {onTranslateSubtitles && (
                                            <button
                                                onClick={() => onTranslateSubtitles(activeSubtitleLanguage)}
                                                disabled={translationsInProgress.has(activeSubtitleLanguage)}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {translationsInProgress.has(activeSubtitleLanguage) ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Traduction en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Languages className="w-4 h-4" />
                                                        Traduire dans cette langue
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed w-full"><p className="text-sm text-gray-500">Sélectionnez un clip pour éditer ses sous-titres.</p></div>
                </div>
            )}
        </div >
    );
};

export default SubtitlesTab;
